import React, {useState} from 'react';
import Button from "components/Button";
import {TrashIcon} from "@heroicons/react/solid";
import Field from "components/Form/Field";
import Toggle from "components/Form/Toggle";
import FieldHeader from "components/Form/FieldHeader";
import Uploader from "components/Form/Uploader";
import InformationListField from "components/Form/InformationEditor";
import Well from "components/Well";
import {FieldArrayRenderProps} from "formik";
import {ElementData} from "types/Element";
import {MediaFile} from "types/MediaFile";
import FactEditor from "./FactEditor";
import LinkEditor from "../../../components/Form/LinkEditor";
import {TextAreaField} from "../../../components/Form/Form";

// Component ----

type ElementFormProps = {
  element: ElementData
  media: Array<MediaFile>
  index: number
  helpers: FieldArrayRenderProps
  name?: string
}

function ElementForm({element, media, index, helpers, name = 'elements'}: ElementFormProps) {
  const [hasLink, setHasLink] = useState<boolean>(!!element.link);

  const toggleLink = (checked: boolean) => {
    void helpers.form.setFieldValue(`${name}.${index}.link`, checked ? {text: '', url: ''} : null)
    setHasLink(checked)
  }

  return (
    <Well
      heading={`Element ${index + 1}`}
      action={
        <Button
          color={Button.colors.WHITE}
          size={Button.sizes.X_SMALL}
          icon={TrashIcon}
          onClick={() => helpers.remove(index)}
        />
      }>

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 lg:grid-cols-6">

        {/* Name */}
        <div className="sm:col-span-4 xl:col-span-3">
          <Field
            label="Name *"
            type="text"
            hint="250 chars max"
            name={`${name}.${index}.name`}
            maxLength={250}
          />
        </div>

        {/* Caption */}
        <div className="sm:col-span-4 xl:col-span-3">
          <Field
            label="Caption"
            hint="Optional. 50 chars max."
            type="text"
            name={`${name}.${index}.caption`}
            maxLength={50}
          />
        </div>

        {/* Description */}
        <div className="sm:col-span-6">
          <TextAreaField
            label="Description"
            hint="Optional. 250 chars max."
            rows={4}
            name={`${name}.${index}.description`}
            maxChars={250}
          />
        </div>

        {/* Link */}
        <div className="sm:col-span-6">
          <Toggle
            label="Link"
            checked={hasLink}
            onChange={toggleLink}
          />

          {hasLink &&
            <div className="mt-2">
              <LinkEditor name={`${name}.${index}.link`} dark/>
            </div>
          }
        </div>

        {/* Media */}
        <div className="sm:col-span-6">
          <FieldHeader label="Media" hint="optional"/>
          <Uploader
            id={`${name}-${index}-media`}
            type={[Uploader.types.IMAGE, Uploader.types.AUDIO, Uploader.types.VIDEO]}
            files={media ?? []}
            onChange={files => helpers
              .form
              .setFieldValue(`${name}.${index}.media`, files.map(file => ({id: file.id})))
            }
          />
        </div>

        {/* Facts */}
        <div className="sm:col-span-6">
          <FieldHeader label="Additional Facts"/>
          <div className="mt-1">
            <FactEditor name={`${name}.${index}.facts`} dark/>
          </div>
        </div>

        {/* Information */}
        <div className="sm:col-span-6">
          <FieldHeader label="Additional Information"/>
          <div className="mt-1">
            <InformationListField name={`${name}.${index}.information`} information={element.information}/>
          </div>
        </div>

        {/* Featured */}
        <div className="sm:col-span-6">
          <Toggle
            label="Featured"
            checked={element.is_featured ?? false}
            onChange={checked => helpers
              .form
              .setFieldValue(`${name}.${index}.is_featured`, checked)
            }
          />
        </div>
      </div>
    </Well>
  );
}

export default ElementForm;